import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);

  useEffect(() => {
    // Get initial session — wrapped in try/catch to handle network failures gracefully
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session?.user?.id);
      } else {
        setLoading(false);
      }
    })?.catch(() => {
      // Network failure on initial session fetch — treat as logged out
      setUser(null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      // When token refresh fails, clear the session and treat user as logged out
      if (_event === 'TOKEN_REFRESH_FAILED') {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session?.user?.id);
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching profile:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          console.log('No profile found for user');
          setUserProfile(null);
        } else {
          console.log('Error fetching profile:', error?.message);
          setUserProfile(null);
        }
      } else {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName, role) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role
          }
        }
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during signup:', error?.message);
          throw error;
        } else {
          throw new Error(error?.message || 'Signup failed');
        }
      }

      return { data, error: null };
    } catch (error) {
      console.log('Signup error:', error?.message);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during signin:', error?.message);
          throw error;
        } else {
          throw new Error(error?.message || 'Invalid email or password');
        }
      }

      return { data, error: null };
    } catch (error) {
      console.log('Signin error:', error?.message);
      return { data: null, error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location?.href,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        throw new Error(error?.message || 'Google sign-in failed');
      }

      return { data, error: null };
    } catch (error) {
      console.log('Google signin error:', error?.message);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during signout:', error?.message);
          throw error;
        } else {
          throw new Error(error?.message || 'Signout failed');
        }
      }
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.log('Signout error:', error?.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email);
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during password reset:', error?.message);
          throw error;
        } else {
          throw new Error(error?.message || 'Password reset failed');
        }
      }
    } catch (error) {
      console.log('Password reset error:', error?.message);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    isLoginModalOpen,
    openLoginModal,
    closeLoginModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
